import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, FileText, Save, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateContractPage = () => {
  const { collaborationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const contractType = searchParams.get('type') || 'time_material';

  const [collaboration, setCollaboration] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [contractContent, setContractContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchCollaboration();
    fetchTemplates();
  }, [collaborationId]);

  const fetchCollaboration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/collaborations/${collaborationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollaboration(response.data);
    } catch (error) {
      console.error('Error fetching collaboration:', error);
      toast.error('Failed to load collaboration');
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contract-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      
      // Auto-select template based on type
      const template = response.data.find(t => {
        if (contractType === 'nda') return t.type === 'nda';
        if (contractType === 'non_compete') return t.type === 'non_compete';
        if (contractType === 'time_material') return t.type === 'regie';
        if (contractType === 'fixed_price') return t.type === 'project_based';
        return false;
      });
      
      if (template) {
        setSelectedTemplate(template);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateContract = async () => {
    if (!selectedTemplate || !collaboration) return;

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/collaborations/${collaborationId}/preview-contract`,
        {
          template_id: selectedTemplate.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setContractContent(response.data.content);
      toast.success('Contract generated with collaboration data!');
    } catch (error) {
      console.error('Error generating contract:', error);
      toast.error('Failed to generate contract');
    } finally {
      setGenerating(false);
    }
  };

  const saveAndCreateContract = async () => {
    if (!contractContent.trim()) {
      toast.error('Contract content cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/collaborations/${collaborationId}/contract`,
        {
          content: contractContent,
          template_id: selectedTemplate?.id,
          type: contractType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contract created! Both parties can now sign.');
      navigate(`/collaborations`);
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const getContractTypeLabel = () => {
    switch (contractType) {
      case 'nda': return 'Non-Disclosure Agreement (NDA)';
      case 'non_compete': return 'Non-Compete & Non-Solicitation Agreement';
      case 'time_material': return 'Time & Material Contract';
      case 'fixed_price': return 'Fixed Price Project Contract';
      default: return 'Contract';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Button
        variant="ghost"
        onClick={() => navigate('/collaborations')}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Collaborations
      </Button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#1B3240]">Create Contract</h1>
        <p className="text-gray-600 mt-2">{getContractTypeLabel()}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contract Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!contractContent ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">
                    Generate a contract from the selected template with auto-filled collaboration data
                  </p>
                  <Button
                    className="btn-snow"
                    onClick={generateContract}
                    disabled={!selectedTemplate || generating}
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate Contract
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Contract Content (HTML)</label>
                    <Textarea
                      value={contractContent}
                      onChange={(e) => setContractContent(e.target.value)}
                      rows={20}
                      className="font-mono text-sm"
                      placeholder="Contract content will appear here..."
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      className="btn-snow flex-1"
                      onClick={saveAndCreateContract}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Create & Send for Signatures
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setContractContent('')}
                    >
                      Reset
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {collaboration && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Collaboration Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600">Project</p>
                  <p className="font-medium">{collaboration.project?.title}</p>
                </div>
                <div>
                  <p className="text-gray-600">Resource</p>
                  <p className="font-medium">
                    {collaboration.resource?.first_name} {collaboration.resource?.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">PM Company</p>
                  <p className="font-medium">{collaboration.pm_company?.name}</p>
                </div>
                <div>
                  <p className="text-gray-600">RM Company</p>
                  <p className="font-medium">{collaboration.rm_company?.name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Daily Rate</p>
                  <p className="font-medium">
                    {collaboration.resource?.currency} {collaboration.resource?.daily_rate}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Selected Template</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedTemplate ? (
                <div className="space-y-2">
                  <p className="font-medium">{selectedTemplate.name}</p>
                  <p className="text-sm text-gray-600">{selectedTemplate.description}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      // Show template selection
                      setSelectedTemplate(null);
                    }}
                  >
                    Change Template
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.filter(t => {
                    if (contractType === 'nda') return t.type === 'nda';
                    if (contractType === 'non_compete') return t.type === 'non_compete';
                    return ['regie', 'project_based', 'freelance'].includes(t.type);
                  }).map(template => (
                    <Button
                      key={template.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setSelectedTemplate(template)}
                    >
                      {template.name}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Templates Disclaimer */}
          <p className="text-xs text-gray-500 text-center">
            Templates are provided for convenience only and must be independently reviewed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateContractPage;
